import styled, { keyframes } from 'styled-components';
import { theme } from '../../theme';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const PageHero = styled.section`
  padding: 140px 2rem 80px;
  background: linear-gradient(180deg, ${theme.colors.bg} 0%, #101722 100%);
  position: relative;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 70% 60% at 50% 0%, rgba(${theme.colors.primaryRgb}, 0.1) 0%, transparent 70%),
      linear-gradient(rgba(148, 163, 184, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.05) 1px, transparent 1px);
    background-size: auto, 56px 56px, 56px 56px;
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), transparent 78%);
  }
`;

export const PageContainer = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 1rem;
  }
`;

export const PageHeroShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 2rem;
  align-items: end;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const PageHeroCopy = styled.div`
  animation: ${fadeUp} 0.55s ease both;
`;

export const PageTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.primaryLight};
  background: rgba(${theme.colors.primaryRgb}, 0.1);
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.25);
  margin-bottom: 1.25rem;
`;

export const PageTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  letter-spacing: -0.06em;
  color: ${theme.colors.text};
  margin-bottom: 1rem;

  span {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const PageSubtitle = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.textMuted};
  max-width: 560px;
  margin: 0;
  line-height: 1.75;
`;

export const PageLeadCard = styled.aside`
  padding: 1.2rem;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(10, 15, 22, 0.9));
  animation: ${fadeUp} 0.55s ease 0.08s both;
`;

export const LeadLabel = styled.span`
  display: inline-flex;
  margin-bottom: 0.45rem;
  color: ${theme.colors.primaryLight};
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const LeadTitle = styled.h2`
  margin-bottom: 0.45rem;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.04em;
  color: ${theme.colors.text};
`;

export const LeadText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 0.92rem;
  line-height: 1.7;
  margin-bottom: 0.85rem;
`;

export const HeroMetrics = styled.div`
  display: grid;
  gap: 0.65rem;
`;

export const HeroMetric = styled.div`
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(8, 12, 18, 0.62);

  span {
    display: block;
    margin-bottom: 0.3rem;
    color: ${theme.colors.textMuted};
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    color: ${theme.colors.textSecondary};
    font-size: 0.88rem;
    line-height: 1.45;
  }
`;

export const ServicesDetailSection = styled.section`
  background: ${theme.colors.bgSecondary};
  padding: 80px 0;
`;

export const DetailGrid = styled.div`
  display: grid;
  gap: 1.4rem;
`;

export const DetailCard = styled.div<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: center;
  padding: 1rem;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(10, 15, 22, 0.9));
  ${({ $reverse }) => $reverse && 'direction: rtl;'}

  & > * { direction: ltr; }

  &:hover {
    border-color: rgba(${theme.colors.primaryRgb}, 0.18);
    transform: translateY(-2px);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    direction: ltr;
  }
`;

export const DetailVisual = styled.div<{ $bg: string }>`
  height: 300px;
  border-radius: 22px;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 5rem;
  border: 1px solid rgba(148, 163, 184, 0.1);
  animation: ${fadeUp} 0.6s ease both;
`;

export const DetailContent = styled.div`
  animation: ${fadeUp} 0.6s ease 0.1s both;
  padding: 0.35rem 0.2rem;
`;

export const DetailTag = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 0.2rem 0.7rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}18`};
  border: 1px solid ${({ $color }) => `${$color}30`};
  margin-bottom: 1rem;
`;

export const DetailTitle = styled.h2`
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 700;
  letter-spacing: -0.05em;
  color: ${theme.colors.text};
  margin-bottom: 0.75rem;
`;

export const DetailDesc = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.textMuted};
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

export const FeatureList = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.7rem 0.8rem;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.08);
  background: rgba(8, 12, 18, 0.56);
  font-size: 0.85rem;
  color: ${theme.colors.textSecondary};

  svg { color: ${theme.colors.primaryLight}; flex-shrink: 0; margin-top: 0.15rem; }
`;
